import type { ColumnDef } from '@tanstack/react-table';

import { type GetSubventionsQuery } from 'data/__generated__';
import { textColumn, multiSelectColumn } from 'components/DataTable';

import { renderAsPrice } from 'helpers/price';

import { StatusTag } from 'pages/Subventions/components/StatusTag';
import { getPeriodLabel, getSubventionState, SubventionState, SUBVENTION_STATE_LABELS } from './states';

export type SubventionRow = GetSubventionsQuery['subventions'][number];

export const NO_ORGANIZATION_LABEL = 'sans organisation';

type SubventionFilterInput = {
    searchValue: string;
    state: SubventionState[];
    organizationName: string[];
};

type OrganizationOption = { value: string; label: string };

export const getSubventionsColumns = (
    organizationOptions: OrganizationOption[],
): ColumnDef<SubventionRow, unknown>[] => [
    textColumn<SubventionRow, SubventionFilterInput>({
        accessorKey: 'name',
        header: 'Nom',
        defaultVisible: true,
    }),
    textColumn<SubventionRow, SubventionFilterInput>({
        accessorKey: 'currentAmount',
        header: 'Montant',
        defaultVisible: true,
        cell: ({ row }) => {
            const { currentAmount, reload } = row.original;
            return currentAmount
                ? `${renderAsPrice(currentAmount)}${reload ? ' / ' + getPeriodLabel(reload) : ''}`
                : 'illimité';
        },
    }),
    multiSelectColumn<SubventionRow, SubventionFilterInput>({
        id: 'organizationName',
        accessorFn: (row) => row.organization?.name || NO_ORGANIZATION_LABEL,
        header: 'Organisation',
        filterKey: 'organizationName',
        defaultVisible: true,
        options: organizationOptions,
        cell: ({ row }) => row.original.organization?.name || NO_ORGANIZATION_LABEL,
    }),
    multiSelectColumn<SubventionRow, SubventionFilterInput>({
        id: 'state',
        accessorFn: (row) => getSubventionState(row.archivedAt),
        header: 'Statut',
        filterKey: 'state',
        defaultVisible: true,
        defaultFilterValue: [SubventionState.Regular],
        options: Object.values(SubventionState).map((state) => ({
            value: state,
            label: SUBVENTION_STATE_LABELS[state],
        })),
        renderOption: (option) => <StatusTag state={option.value as SubventionState} />,
        cell: ({ row }) => <StatusTag state={getSubventionState(row.original.archivedAt)} />,
    }),
];
